import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMetadata } from '@/context/MetadataContext';
import { extractIdFromLink } from '@/services/api';
import type { WorkPackage } from '@/types';

export interface DossierGanttItem {
  id: string;
  label: string;
  category: 'achizitii' | 'contracte' | 'livrabile' | 'implementare';
  startDate: string | null;
  endDate: string | null;
  status?: string;
}

interface GanttChartProps {
  workPackages: WorkPackage[];
  dossierItems?: DossierGanttItem[];
}

const MONTH_NAMES = ['Ian', 'Feb', 'Mar', 'Apr', 'Mai', 'Iun', 'Iul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CATEGORY_COLORS: Record<string, { bg: string; border: string }> = {
  achizitii: { bg: 'bg-orange-500', border: 'border-orange-600' },
  contracte: { bg: 'bg-indigo-500', border: 'border-indigo-600' },
  livrabile: { bg: 'bg-teal-500', border: 'border-teal-600' },
  implementare: { bg: 'bg-rose-500', border: 'border-rose-600' },
};

const CATEGORY_LABELS: Record<string, string> = {
  achizitii: 'Achiziții',
  contracte: 'Contracte',
  livrabile: 'Livrabile',
  implementare: 'Implementare',
};

export function GanttChart({ workPackages, dossierItems = [] }: GanttChartProps) {
  const navigate = useNavigate();
  const { getTypeById, getStatusById } = useMetadata();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Collect all dates for range calculation
  const { minDate, maxDate, totalDays, workPackagesWithDates, dossierWithDates } = useMemo(() => {
    const wpWithDates = workPackages
      .filter(wp => wp.startDate || wp.dueDate)
      .map(wp => ({
        ...wp,
        start: wp.startDate ? new Date(wp.startDate) : null,
        end: wp.dueDate ? new Date(wp.dueDate) : null,
      }))
      .sort((a, b) => {
        const dateA = a.start || a.end || new Date();
        const dateB = b.start || b.end || new Date();
        return dateA.getTime() - dateB.getTime();
      });

    const dWithDates = dossierItems
      .filter(d => d.startDate || d.endDate)
      .map(d => ({
        ...d,
        start: d.startDate ? new Date(d.startDate) : null,
        end: d.endDate ? new Date(d.endDate) : null,
      }))
      .sort((a, b) => {
        const dateA = a.start || a.end || new Date();
        const dateB = b.start || b.end || new Date();
        return dateA.getTime() - dateB.getTime();
      });

    const allItems = [...wpWithDates, ...dWithDates];

    if (allItems.length === 0) {
      const today = new Date();
      return {
        minDate: today,
        maxDate: new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000),
        totalDays: 30,
        workPackagesWithDates: [],
        dossierWithDates: [],
      };
    }

    const allDates = allItems.flatMap(item => [item.start, item.end].filter(Boolean) as Date[]);
    const min = new Date(Math.min(...allDates.map(d => d.getTime())));
    const max = new Date(Math.max(...allDates.map(d => d.getTime())));

    min.setDate(min.getDate() - 3);
    max.setDate(max.getDate() + 3);

    const days = Math.ceil((max.getTime() - min.getTime()) / (24 * 60 * 60 * 1000));

    return {
      minDate: min,
      maxDate: max,
      totalDays: Math.max(days, 14),
      workPackagesWithDates: wpWithDates,
      dossierWithDates: dWithDates,
    };
  }, [workPackages, dossierItems]);

  // Group dossier items by category
  const dossierGroups = useMemo(() => {
    const groups: Record<string, typeof dossierWithDates> = {};
    for (const item of dossierWithDates) {
      if (!groups[item.category]) groups[item.category] = [];
      groups[item.category].push(item);
    }
    return groups;
  }, [dossierWithDates]);

  const months = useMemo(() => {
    const result: { name: string; year: number; width: number }[] = [];
    const current = new Date(minDate);
    current.setDate(1);

    while (current <= maxDate) {
      const monthStart = new Date(current);
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
      const visibleStart = Math.max(monthStart.getTime(), minDate.getTime());
      const visibleEnd = Math.min(monthEnd.getTime(), maxDate.getTime());
      const visibleDays = Math.ceil((visibleEnd - visibleStart) / (24 * 60 * 60 * 1000)) + 1;
      const widthPercent = (visibleDays / totalDays) * 100;

      result.push({
        name: MONTH_NAMES[current.getMonth()],
        year: current.getFullYear(),
        width: widthPercent,
      });

      current.setMonth(current.getMonth() + 1);
    }

    return result;
  }, [minDate, maxDate, totalDays]);

  const getBarStyle = (start: Date | null, end: Date | null) => {
    const s = start || end || minDate;
    const e = end || start || minDate;

    const startOffset = Math.max(0, (s.getTime() - minDate.getTime()) / (24 * 60 * 60 * 1000));
    const duration = Math.max(1, (e.getTime() - s.getTime()) / (24 * 60 * 60 * 1000) + 1);

    const leftPercent = (startOffset / totalDays) * 100;
    const widthPercent = (duration / totalDays) * 100;

    return {
      left: `${leftPercent}%`,
      width: `${Math.max(widthPercent, 2)}%`,
    };
  };

  const formatDateShort = (date: Date | null) => {
    if (!date) return '-';
    return `${date.getDate()} ${MONTH_NAMES[date.getMonth()]}`;
  };

  const getTypeColor = (typeId: number | null) => {
    const type = typeId ? getTypeById(typeId) : null;
    if (!type) return { bg: 'bg-slate-500', border: 'border-slate-600' };

    const name = type.name.toLowerCase();
    if (name.includes('fază') || name.includes('phase')) return { bg: 'bg-blue-600', border: 'border-blue-700' };
    if (name.includes('reper') || name.includes('milestone')) return { bg: 'bg-purple-600', border: 'border-purple-700' };
    if (name.includes('activitate') || name.includes('task')) return { bg: 'bg-emerald-600', border: 'border-emerald-700' };
    return { bg: 'bg-slate-500', border: 'border-slate-600' };
  };

  const isCompleted = (statusId: number | null) => {
    const status = statusId ? getStatusById(statusId) : null;
    return status?.isClosed || false;
  };

  const todayPosition = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (today >= minDate && today <= maxDate) {
      const offset = (today.getTime() - minDate.getTime()) / (24 * 60 * 60 * 1000);
      return (offset / totalDays) * 100;
    }
    return null;
  }, [minDate, maxDate, totalDays]);

  const hasAnyData = workPackagesWithDates.length > 0 || dossierWithDates.length > 0;

  if (!hasAnyData) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p className="text-base">Nu există activități cu date de început sau termen limită.</p>
        <p className="text-sm mt-2">Adăugați date activităților pentru a le vedea în Gantt.</p>
      </div>
    );
  }

  const renderTodayLine = () =>
    todayPosition !== null ? (
      <div className="absolute top-0 bottom-0 w-px bg-red-400 z-10" style={{ left: `${todayPosition}%` }} />
    ) : null;

  const renderBar = (
    start: Date | null,
    end: Date | null,
    label: string,
    colors: { bg: string; border: string },
    isHovered: boolean,
    completed = false,
  ) => {
    const barStyle = getBarStyle(start, end);
    const s = start || end || minDate;
    const e = end || start || minDate;
    const duration = Math.max(1, (e.getTime() - s.getTime()) / (24 * 60 * 60 * 1000) + 1);
    const isSmallBar = duration <= 14;

    return (
      <>
        <div
          className={`absolute h-5 rounded border ${colors.bg} ${colors.border}
            ${completed ? 'opacity-50' : ''}
            ${isHovered ? 'ring-2 ring-blue-400 ring-offset-1' : ''}
            shadow-sm transition-all`}
          style={{ ...barStyle, top: '50%', transform: 'translateY(-50%)' }}
          title={`${label}\n${formatDateShort(start)} - ${formatDateShort(end)}`}
        >
          {!isSmallBar && (
            <span className="absolute inset-0 flex items-center justify-center text-[10px] text-white font-semibold px-1 truncate drop-shadow-sm">
              {formatDateShort(start)} → {formatDateShort(end)}
            </span>
          )}
        </div>
        {isSmallBar && (
          <span
            className="absolute text-[10px] text-gray-600 font-medium whitespace-nowrap"
            style={{
              left: barStyle.left,
              top: '50%',
              transform: 'translateY(-50%) translateX(calc(100% + 4px))',
              marginLeft: barStyle.width,
            }}
          >
            {formatDateShort(start)} → {formatDateShort(end)}
          </span>
        )}
      </>
    );
  };

  const dossierCategoryOrder = ['achizitii', 'contracte', 'livrabile', 'implementare'];

  return (
    <div className="space-y-3">
      {/* Legendă */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4 text-xs flex-wrap">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-blue-600"></div>
            <span className="text-gray-600">Fază</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-emerald-600"></div>
            <span className="text-gray-600">Activitate</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-purple-600"></div>
            <span className="text-gray-600">Reper</span>
          </div>
          {dossierWithDates.length > 0 && (
            <>
              <div className="w-px h-3 bg-gray-300"></div>
              {dossierCategoryOrder.filter(cat => dossierGroups[cat]).map(cat => (
                <div key={cat} className="flex items-center gap-1.5">
                  <div className={`w-3 h-3 rounded ${CATEGORY_COLORS[cat].bg}`}></div>
                  <span className="text-gray-600">{CATEGORY_LABELS[cat]}</span>
                </div>
              ))}
            </>
          )}
          <div className="flex items-center gap-1.5">
            <div className="w-0.5 h-3 bg-red-500"></div>
            <span className="text-gray-600">Astăzi</span>
          </div>
        </div>
        <div className="text-xs text-gray-500">
          {workPackagesWithDates.length} activități{dossierWithDates.length > 0 ? ` + ${dossierWithDates.length} dosar` : ''}
        </div>
      </div>

      {/* Chart */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <div style={{ minWidth: `${Math.max(600, totalDays * 2.5 + 256)}px` }}>
            {/* Month headers */}
            <div className="flex bg-gray-100 border-b border-gray-200">
              <div className="w-48 md:w-64 flex-shrink-0 px-3 py-2 text-xs font-semibold text-gray-700 border-r border-gray-200 bg-gray-100 sticky left-0 z-20">
                Element
              </div>
              <div className="flex-1 flex">
                {months.map((month, idx) => (
                  <div
                    key={idx}
                    className="text-center text-xs font-medium text-gray-600 py-2 border-r border-gray-100 last:border-r-0"
                    style={{ width: `${month.width}%`, minWidth: '60px' }}
                  >
                    {month.name} {month.year !== new Date().getFullYear() ? `'${String(month.year).slice(-2)}` : ''}
                  </div>
                ))}
              </div>
            </div>

            {/* Rows */}
            <div className="max-h-[600px] overflow-y-auto">
              {/* Work packages */}
              {workPackagesWithDates.map((wp) => {
                const typeId = extractIdFromLink(wp._links.type);
                const statusId = extractIdFromLink(wp._links.status);
                const type = typeId ? getTypeById(typeId) : null;
                const status = statusId ? getStatusById(statusId) : null;
                const colors = getTypeColor(typeId);
                const completed = isCompleted(statusId);
                const isHovered = hoveredId === `wp-${wp.id}`;

                return (
                  <div
                    key={`wp-${wp.id}`}
                    className={`flex border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors
                      ${isHovered ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                    onClick={() => navigate(`/work-packages/${wp.id}`)}
                    onMouseEnter={() => setHoveredId(`wp-${wp.id}`)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    <div className={`w-48 md:w-64 flex-shrink-0 px-3 py-2 border-r border-gray-200 sticky left-0 z-10 ${isHovered ? 'bg-blue-50' : 'bg-white'}`}>
                      <div className="flex items-start gap-1.5">
                        <span className="text-[10px] text-gray-400 flex-shrink-0 mt-0.5">#{wp.id}</span>
                        <div className="min-w-0 flex-1">
                          <p className={`text-xs font-medium leading-tight ${isHovered ? 'text-blue-700' : 'text-gray-900'} line-clamp-2`}>
                            {wp.subject}
                          </p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: type?.color || '#6b7280' }}></span>
                            <span className="text-[10px] text-gray-500 truncate">{status?.name || '-'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 relative py-2">
                      {renderTodayLine()}
                      {renderBar(wp.start, wp.end, wp.subject, colors, isHovered, completed)}
                    </div>
                  </div>
                );
              })}

              {/* Dossier items by category */}
              {dossierCategoryOrder.filter(cat => dossierGroups[cat]).map(cat => (
                <React.Fragment key={cat}>
                  {/* Category header */}
                  <div className="flex border-b border-gray-200 bg-gray-50">
                    <div className="w-48 md:w-64 flex-shrink-0 px-3 py-1.5 border-r border-gray-200 sticky left-0 z-10 bg-gray-50">
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded ${CATEGORY_COLORS[cat].bg}`}></div>
                        <span className="text-xs font-semibold text-gray-700">{CATEGORY_LABELS[cat]}</span>
                        <span className="text-[10px] text-gray-500">({dossierGroups[cat].length})</span>
                      </div>
                    </div>
                    <div className="flex-1 relative py-1.5">
                      {renderTodayLine()}
                    </div>
                  </div>
                  {/* Category items */}
                  {dossierGroups[cat].map(item => {
                    const isHovered = hoveredId === item.id;
                    const colors = CATEGORY_COLORS[cat];
                    const isDone = item.status === 'finalizat' || item.status === 'aprobat' || item.status === 'completed';
                    return (
                      <div
                        key={item.id}
                        className={`flex border-b border-gray-100 last:border-b-0 transition-colors
                          ${isHovered ? 'bg-gray-50' : ''}`}
                        onMouseEnter={() => setHoveredId(item.id)}
                        onMouseLeave={() => setHoveredId(null)}
                      >
                        <div className={`w-48 md:w-64 flex-shrink-0 px-3 py-2 border-r border-gray-200 sticky left-0 z-10 ${isHovered ? 'bg-gray-50' : 'bg-white'}`}>
                          <div className="min-w-0 pl-4">
                            <p className={`text-xs font-medium leading-tight text-gray-900 line-clamp-2`}>
                              {item.label}
                            </p>
                            {item.status && (
                              <span className="text-[10px] text-gray-500">{item.status}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex-1 relative py-2">
                          {renderTodayLine()}
                          {renderBar(item.start, item.end, item.label, colors, isHovered, isDone)}
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-400 text-center">
        Click pe o activitate pentru a vedea detaliile
      </div>
    </div>
  );
}
